import { rewriteMatrixHomeserverUrl } from '@/modules/chat-matrix-fast/config'
import {
  avatarDebugLog,
  summarizeAvatarUrlForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export interface UserDataSource {
  user_id?: string | number
  user?: string | number | {
    id?: string | number
    user?: string | number
  }
  avatar?: string
  created_by?: {
    avatar?: string
    photo?: string
    full_name?: string
    user_name?: string
  }
  user_name?: string
  full_name?: string
  name?: string
}

export interface ChatMemberSource {
  user?: string | number | {
    id?: string | number
    uuid?: string
    avatar?: string
    photo?: string
    profile_picture?: string
  }
  user_id?: string | number
  user_uuid?: string
  avatar?: string
  photo?: string
  profile_picture?: string
}

export const extractUserId = (source: UserDataSource): string | number => {
  if (source.user_id) {
    return source.user_id
  }

  if (typeof source.user === 'string' || typeof source.user === 'number') {
    return source.user
  }

  if (source.user && typeof source.user === 'object') {
    return source.user.id || source.user.user || ''
  }

  return ''
}

export const extractUserName = (source: UserDataSource): string => {
  return (
    source.user_name
    || source.full_name
    || source.name
    || (source as any).created_by?.full_name
    || (source as any).created_by?.user_name
    || 'Unknown User'
  )
}

export const extractUserAvatar = (
  source: UserDataSource,
  members?: ChatMemberSource[],
): string | null => {
  let avatar
    = source.avatar
    || (source as any).avatar
    || source.created_by?.avatar
    || (source as any).user?.avatar
    || (source as any).user?.icon
    || (source as any).user?.photo
    || source.created_by?.photo

  if (!avatar && members && Array.isArray(members) && members.length > 0) {
    const userId = extractUserId(source)

    if (userId) {
      const needle = String(userId)
      const member = members.find(m => {
        const ids = [
          typeof m.user === 'object' ? m.user?.id : undefined,
          typeof m.user === 'object' ? m.user?.uuid : undefined,
          typeof m.user === 'string' || typeof m.user === 'number' ? m.user : undefined,
          m.user_id,
          m.user_uuid,
        ]

        return ids.some(id => id != null && String(id) === needle)
      })

      if (member) {
        avatar
          = member.avatar
          || member.photo
          || member.profile_picture

        if (!avatar && typeof member.user === 'object' && member.user) {
          avatar
            = member.user.avatar
            || member.user.photo
            || member.user.profile_picture
        }
      }
    }
  }

  return normalizeAvatarUrl(avatar)
}

export const normalizeAvatarUrl = (avatar: string | null | undefined): string | null => {
  if (!avatar || typeof avatar !== 'string') {
    return null
  }

  avatar = avatar.trim()
  if (!avatar) {
    return null
  }

  if (avatar.includes('/_matrix/')) {
    return rewriteMatrixHomeserverUrl(avatar)
  }

  if (avatar.startsWith('http') || avatar.startsWith('//')) {
    return avatar
  }

  const baseUrl = import.meta.env.VITE_BASE_URL || ''
  if (baseUrl && !avatar.startsWith(baseUrl)) {
    const separator = avatar.startsWith('/') ? '' : '/'

    return `${baseUrl}${separator}${avatar}`
  }

  return avatar
}

function toAbsoluteUrl(value: string): URL | null {
  try {
    return new URL(value, typeof window !== 'undefined' ? window.location.origin : 'https://local')
  } catch {
    return null
  }
}

function isPresignedQuery(url: URL): boolean {
  const query = url.search.toLowerCase()
  return query.includes('x-amz-signature=')
    || query.includes('x-amz-algorithm=')
    || query.includes('x-amz-credential=')
    || query.includes('signature=')
}

/** Path key so `/daomed/...` and `https://s3.../daomed/...` count as the same file. */
function avatarAssetKey(url: URL): string {
  const path = url.pathname
  const daomedIdx = path.indexOf('/daomed/')
  if (daomedIdx >= 0) {
    return path.slice(daomedIdx)
  }
  return path
}

/** Stable key for warm-paint cache across list/header AuthenticatedVImg instances. */
export function getAvatarAssetKey(src: string | null | undefined): string | null {
  const normalized = normalizeAvatarUrl(src)
  if (!normalized) {
    return null
  }
  const absolute = toAbsoluteUrl(normalized)
  if (!absolute) {
    return null
  }
  const key = avatarAssetKey(absolute)
  return key.includes('/') ? key : null
}

export function isSameAvatarAsset(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = normalizeAvatarUrl(left)
  const b = normalizeAvatarUrl(right)
  if (!a || !b) {
    return false
  }
  if (a === b) {
    return true
  }

  const ua = toAbsoluteUrl(a)
  const ub = toAbsoluteUrl(b)
  if (!ua || !ub) {
    return false
  }

  if (ua.origin === ub.origin && ua.pathname === ub.pathname) {
    return true
  }

  // Relative app path vs absolute CDN/S3 for the same object.
  return avatarAssetKey(ua) === avatarAssetKey(ub)
    && avatarAssetKey(ua).includes('/')
}

/** Keep current URL when next is the same asset with a new presigned query. */
export function preferStableAvatar(
  current: string | null | undefined,
  next: string | null | undefined,
): string | null {
  const normalizedNext = normalizeAvatarUrl(next)
  const normalizedCurrent = normalizeAvatarUrl(current)

  if (!normalizedNext) {
    return normalizedCurrent
  }
  if (!normalizedCurrent) {
    return normalizedNext
  }
  if (isSameAvatarAsset(normalizedCurrent, normalizedNext)) {
    return normalizedCurrent
  }

  const ua = toAbsoluteUrl(normalizedCurrent)
  const ub = toAbsoluteUrl(normalizedNext)
  if (
    ua
    && ub
    && ua.origin === ub.origin
    && isPresignedQuery(ua)
    && isPresignedQuery(ub)
    && ua.pathname === ub.pathname
  ) {
    return normalizedCurrent
  }

  avatarDebugLog('preferStable · take next (different asset)', {
    from: summarizeAvatarUrlForLog(normalizedCurrent),
    to: summarizeAvatarUrlForLog(normalizedNext),
  })

  return normalizedNext
}

export const normalizeReactionIconUrl = (icon: string | null | undefined): string | null => {
  if (!icon || typeof icon !== 'string') {
    return null
  }

  icon = icon.trim()
  if (!icon) {
    return null
  }

  if (!icon.includes('/') && !icon.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i)) {
    return icon
  }

  if (icon.startsWith('http') || icon.startsWith('//')) {
    return icon
  }

  const baseUrl = import.meta.env.VITE_BASE_URL || ''
  if (baseUrl && !icon.startsWith(baseUrl)) {
    const separator = icon.startsWith('/') ? '' : '/'

    return `${baseUrl}${separator}${icon}`
  }

  return icon
}
